/**
 * Authentication service for Google SSO
 */

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080'

export interface User {
  email: string
  name: string
  picture?: string
}

export interface AuthStatus {
  isAuthenticated: boolean
  user?: User
  error?: string
}

// Cookie functions removed - backend now handles secure cookie setting with HttpOnly flag

/**
 * Get stored auth token
 */
function getStoredToken(): string | null {
  return localStorage.getItem('evop_toolkit_auth_token')
}

/**
 * Store auth token
 */
function storeToken(token: string): void {
  localStorage.setItem('evop_toolkit_auth_token', token)
  // Note: Backend sets secure HttpOnly cookie - no frontend cookie needed
}

/**
 * Clear stored auth token
 */
function clearToken(): void {
  localStorage.removeItem('evop_toolkit_auth_token')
  // Note: Backend handles cookie clearing via logout endpoint
}

/**
 * Handle authentication callback - no longer extracts token from URL
 * Backend now sets secure HttpOnly cookie directly
 * @returns true if we're on an auth callback URL, false otherwise
 */
export function handleTokenFromUrl(): boolean {
  const hash = window.location.hash
  console.log('🔍 Checking for auth callback')
  
  // Clear any token fragments that might still be in URL for security
  if (hash.includes('token=')) {
    const newHash = hash.replace(/[&#]?token=[^&]*/, '')
    window.location.hash = newHash
    console.log('🧹 Cleared token from URL hash for security')
    
    // Return true to indicate this was an auth callback
    // Frontend should now check authentication status via API
    return true
  }
  
  console.log('✅ No auth callback detected')
  return false
}

/**
 * Check if user is currently authenticated
 */
export async function checkAuth(): Promise<AuthStatus> {
  try {
    const token = getStoredToken()
    console.log('🔍 Checking auth - token available:', !!token)
    console.log('🔍 Token preview:', token ? token.substring(0, 20) + '...' : 'none')
    
    const headers: Record<string, string> = {
      'Accept': 'application/json',
    }
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
      console.log('🔍 Added Authorization header')
    } else {
      console.log('⚠️ No token available - request will be unauthenticated')
    }

    // Smart path construction: if API_BASE already ends with /api, don't add it again
    const authUrl = API_BASE.endsWith('/api') ? `${API_BASE}/auth/user` : `${API_BASE}/api/auth/user`
    console.log('🌐 Making auth check request to:', authUrl)
    console.log('🔍 Request headers:', headers)
    
    const response = await fetch(authUrl, {
      credentials: 'include',
      headers,
    })

    console.log('📊 Auth response status:', response.status)
    console.log('📊 Auth response ok:', response.ok)

    if (response.ok) {
      const user = await response.json()
      console.log('👤 User data received:', user)
      
      if (user && user.email) {
        console.log('✅ Authentication successful')
        return { isAuthenticated: true, user }
      } else {
        console.log('❌ No user data in response')
      }
    } else {
      console.log('❌ Auth request failed with status:', response.status)
    }

    // If authentication failed, clear any stored token
    console.log('🧹 Clearing stored token due to failed authentication')
    clearToken()
    return { isAuthenticated: false }
  } catch (error) {
    console.error('❌ Auth check failed with error:', error)
    clearToken()
    return { 
      isAuthenticated: false, 
      error: 'Failed to check authentication status' 
    }
  }
}

/**
 * Initiate login flow
 */
export function login(redirectPath = '/'): void {
  // Smart path construction: if API_BASE already ends with /api, don't add it again
  const authUrl = API_BASE.endsWith('/api') ? `${API_BASE}/auth/login` : `${API_BASE}/api/auth/login`
  const loginUrl = `${authUrl}?redirect=${encodeURIComponent(redirectPath)}`
  
  // Redirect to backend login endpoint
  window.location.href = loginUrl
}

/**
 * Logout user
 */
export async function logout(): Promise<void> {
  try {
    // Clear the stored token
    clearToken()
    
    // Smart path construction: if API_BASE already ends with /api, don't add it again
    const logoutUrl = API_BASE.endsWith('/api') ? `${API_BASE}/auth/logout` : `${API_BASE}/api/auth/logout`
    
    const response = await fetch(logoutUrl, {
      credentials: 'include',
    })
    
    // Redirect to home page regardless of response
    window.location.href = '/'
  } catch (error) {
    console.error('Logout failed:', error)
    // Still redirect to home
    window.location.href = '/'
  }
}

/**
 * Get current user info
 */
export async function getCurrentUser(): Promise<User | null> {
  try {
    const token = getStoredToken()
    console.log('🔍 Getting current user - token available:', !!token)
    
    const headers: Record<string, string> = {
      'Accept': 'application/json',
    }
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
      console.log('🔍 Added Authorization header for getCurrentUser')
    }

    // Smart path construction: if API_BASE already ends with /api, don't add it again
    const userUrl = API_BASE.endsWith('/api') ? `${API_BASE}/auth/user` : `${API_BASE}/api/auth/user`
    
    const response = await fetch(userUrl, {
      credentials: 'include',
      headers,
    })

    console.log('📊 getCurrentUser response status:', response.status)

    if (response.ok) {
      const user = await response.json()
      console.log('👤 getCurrentUser data:', user)
      return user && user.email ? user : null
    }

    // If request failed, clear any stored token
    console.log('🧹 Clearing token due to failed getCurrentUser request')
    clearToken()
    return null
  } catch (error) {
    console.error('❌ getCurrentUser failed:', error)
    clearToken()
    return null
  }
}